/**
 * PURPOSE: Mostrar una vista previa del medallero general consolidado.
 * RESPONSIBILITIES: Agrupar medallas según los vínculos aún no guardados y mostrar las instituciones representadas.
 * COLLABORATORS: MedalConsolidationEditor.jsx.
 * ROUTE: src/area/MedalTableArea/MedalConsolidationPreview.jsx
 */
import React, { useMemo } from "react";
import { FaTrophy } from "react-icons/fa";

const compareRows = (a, b) =>
  (b.medals.gold - a.medals.gold) ||
  (b.medals.silver - a.medals.silver) ||
  (b.medals.bronze - a.medals.bronze) ||
  (b.medals.total - a.medals.total) ||
  String(a.institution.name).localeCompare(String(b.institution.name), "es");

export default function MedalConsolidationPreview({ rows, sourceSummary, candidates }) {
  const preview = useMemo(() => {
    const candidateById = new Map((candidates || []).map((item) => [Number(item.id), item]));
    const medalsBySource = new Map((sourceSummary || []).map((row) => [Number(row.institution?.id), row.medals]));
    const groups = new Map();

    (rows || []).forEach((row) => {
      const source = row.source;
      const targetId = Number(row.targetInstitutionId) || Number(source.id);
      const target = candidateById.get(targetId) || source;
      if (!groups.has(targetId)) {
        groups.set(targetId, {
          institution: target,
          members: [],
          medals: { gold: 0, silver: 0, bronze: 0, total: 0 },
        });
      }
      const group = groups.get(targetId);
      group.members.push(source);
      const medals = medalsBySource.get(Number(source.id)) || { gold: 0, silver: 0, bronze: 0, total: 0 };
      group.medals.gold += Number(medals.gold || 0);
      group.medals.silver += Number(medals.silver || 0);
      group.medals.bronze += Number(medals.bronze || 0);
      group.medals.total += Number(medals.total || 0);
    });

    return Array.from(groups.values()).sort(compareRows).map((row, index) => ({ ...row, rank: index + 1 }));
  }, [rows, sourceSummary, candidates]);

  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><FaTrophy /></span>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Vista previa general</h3>
          <p className="text-sm text-gray-500">El detalle por disciplina no cambia.</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[720px] w-full text-left">
          <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
            <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Institución</th><th className="px-4 py-3">Representa a</th><th className="px-4 py-3 text-center">Oro</th><th className="px-4 py-3 text-center">Plata</th><th className="px-4 py-3 text-center">Bronce</th><th className="px-4 py-3 text-center">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {preview.map((row) => (
              <tr key={row.institution.id}>
                <td className="px-4 py-3 font-bold">{row.rank}</td>
                <td className="px-4 py-3"><div className="font-bold text-gray-900">{row.institution.name}</div><div className="text-xs font-semibold uppercase text-gray-400">{row.institution.abbreviation}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600"><span className="font-bold">{row.members.length} institución{row.members.length === 1 ? "" : "es"}</span><div className="mt-1 max-w-sm text-xs">{row.members.map((item) => item.name).join(", ")}</div></td>
                <td className="px-4 py-3 text-center font-bold text-amber-700">{row.medals.gold}</td>
                <td className="px-4 py-3 text-center font-bold text-slate-700">{row.medals.silver}</td>
                <td className="px-4 py-3 text-center font-bold text-orange-700">{row.medals.bronze}</td>
                <td className="px-4 py-3 text-center font-bold text-gray-900">{row.medals.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
